import { Head, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'

import AdminLayout from '../../../components/AdminLayout'
import CopyButton from '../../../components/admin/CopyButton'

type FunnelRef = { id: number; name: string; slug: string }

type Visitor = {
  id: number
  createdAt: string
  lastSeenAt: string | null
  firstSeenAt: string | null
  funnel: string
  stage: string
  stageLabel: string
  topStage: string
  topStageLabel: string
  deviceType: string | null
  deviceFingerprint: string | null
  browser: string | null
  os: string | null
  location: string | null
  countryCode: string | null
  landing: string | null
  utmSource: string | null
  referrer: string | null
  ipAddress: string | null
  language: string | null
  timezone: string | null
  visits: number
  events: number
}

type Filters = { funnelId: number | null; device: string | null; source: string | null }

type Props = {
  funnels: FunnelRef[]
  deviceTypes: string[]
  sources: string[]
  filters: Filters
  segments: Record<string, number>
  segment: string
  query: string
  page: number
  perPage: number
  total: number
  visitors: Visitor[]
}

/** Funnel order for the progress pips. Mirrors Visitor::STAGES. */
const STAGE_ORDER = [
  'visited',
  'clicked_cta',
  'added_to_cart',
  'reached_checkout',
  'filled_details',
  'purchased',
]

const SEGMENTS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'purchased', label: 'Purchased' },
  { key: 'engaged', label: 'Engaged' },
  { key: 'bots', label: 'Bots' },
]

function formatWhen(iso: string) {
  const date = new Date(iso)
  const minutes = Math.round((Date.now() - date.getTime()) / 60000)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`
  if (minutes < 60 * 24 * 7) return `${Math.round(minutes / 1440)}d ago`

  return date.toLocaleString(undefined, { month: 'short', day: 'numeric' })
}

/** Turn an ISO 3166-1 alpha-2 code into a flag emoji ("US" -> 🇺🇸). */
function flagEmoji(code: string | null) {
  if (!code || code.length !== 2) return ''
  const base = 0x1f1e6
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((char) => base + char.charCodeAt(0) - 65)
  )
}

// Audience list: one row per device, four columns visible, everything else
// behind the row expander so the list stays scannable.
export default function AdminVisitorsIndex({
  funnels,
  deviceTypes,
  sources,
  filters,
  segments,
  segment,
  query,
  page,
  perPage,
  total,
  visitors,
}: Props) {
  const pages = Math.max(1, Math.ceil(total / perPage))
  const hasFilters = Boolean(filters.funnelId || filters.device || filters.source)
  const [filtersOpen, setFiltersOpen] = useState(hasFilters)
  const [openRows, setOpenRows] = useState<Record<number, boolean>>({})
  const [search, setSearch] = useState(query)

  const visit = (overrides: Record<string, string | number | undefined>) => {
    const next: Record<string, string | number | undefined> = {
      q: search || undefined,
      segment: segment !== 'all' ? segment : undefined,
      funnel_id: filters.funnelId ?? undefined,
      device: filters.device ?? undefined,
      source: filters.source ?? undefined,
      page: undefined,
      ...overrides,
    }
    const params = Object.fromEntries(
      Object.entries(next).filter(([, value]) => value !== undefined && value !== '')
    )
    router.get('/admin/visitors', params, { preserveScroll: true, preserveState: true })
  }

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    if (search === query) return

    const timer = setTimeout(() => visit({ q: search || undefined }), 350)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const exportHref = () => {
    const params = new URLSearchParams()
    if (search) params.set('q', search)
    if (segment !== 'all') params.set('segment', segment)
    if (filters.funnelId) params.set('funnel_id', String(filters.funnelId))
    if (filters.device) params.set('device', filters.device)
    if (filters.source) params.set('source', filters.source)
    const qs = params.toString()
    return `/admin/visitors/export${qs ? `?${qs}` : ''}`
  }

  return (
    <AdminLayout>
      <Head title="Visitors — Sick Stuff Admin" />

      <div className="sss-vz">
        <div className="sss-vz-head">
          <div>
            <p className="sss-admin-eyebrow sss-admin-eyebrow--accent">Audience</p>
            <h1 className="sss-vz-head__title">Visitors</h1>
          </div>

          <div className="sss-vz-counts">
            <div>
              <div className="sss-vz-count__k">Unique</div>
              <div className="sss-vz-count__v">{segments.all ?? 0}</div>
            </div>
            <span className="sss-vz-counts__rule" />
            <div>
              <div className="sss-vz-count__k">Purchased</div>
              <div className="sss-vz-count__v sss-vz-count__v--accent">{segments.purchased ?? 0}</div>
            </div>
            <span className="sss-vz-counts__rule" />
            <div>
              <div className="sss-vz-count__k">Bots</div>
              <div className="sss-vz-count__v sss-vz-count__v--muted">{segments.bots ?? 0}</div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="sss-vz-toolbar">
          <div className="sss-vz-toolbar__row">
            <span className="sss-vz-search__icon" aria-hidden>
              ⌕
            </span>
            <input
              className="sss-vz-search"
              placeholder="Search visitor, fingerprint, city…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search visitors"
            />

            <div className="sss-vz-segs">
              {SEGMENTS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`sss-vz-seg ${segment === item.key ? 'is-active' : ''}`}
                  onClick={() => visit({ segment: item.key === 'all' ? undefined : item.key })}
                >
                  {item.label}
                  <span className="sss-vz-seg__n">{segments[item.key] ?? 0}</span>
                </button>
              ))}
            </div>

            <button
              type="button"
              className={`sss-vz-btn ${filtersOpen ? 'is-on' : ''}`}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              Filters {filtersOpen ? '⌃' : '⌄'}
            </button>

            <a className="sss-vz-btn" href={exportHref()}>
              Export
            </a>
          </div>

          {filtersOpen && (
            <div className="sss-vz-filters">
              <Field label="Funnel" value={filters.funnelId ?? ''} onChange={(v) => visit({ funnel_id: v || undefined })}>
                <option value="">All funnels</option>
                {funnels.map((funnel) => (
                  <option key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </option>
                ))}
              </Field>

              <Field label="Device" value={filters.device ?? ''} onChange={(v) => visit({ device: v || undefined })}>
                <option value="">All devices</option>
                {deviceTypes.map((device) => (
                  <option key={device} value={device}>
                    {device}
                  </option>
                ))}
              </Field>

              <Field label="Source" value={filters.source ?? ''} onChange={(v) => visit({ source: v || undefined })}>
                <option value="">All sources</option>
                {sources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </Field>

              {hasFilters && (
                <div className="sss-vz-field" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="sss-vz-btn"
                    onClick={() => visit({ funnel_id: undefined, device: undefined, source: undefined })}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* List */}
        <div className="sss-vz-list">
          <div className="sss-vz-grid sss-vz-list__head">
            <span>Visitor</span>
            <span>Progress</span>
            <span className="sss-vz-hide-sm">Last seen</span>
            <span className="sss-vz-hide-sm">Location</span>
            <span />
          </div>

          {visitors.length === 0 ? (
            <div className="sss-vz-empty">
              {query || hasFilters || segment !== 'all'
                ? 'No visitors match this view.'
                : 'No visitors yet. Browse the storefront to generate some.'}
            </div>
          ) : (
            visitors.map((visitor) => (
              <Row
                key={visitor.id}
                visitor={visitor}
                open={!!openRows[visitor.id]}
                onToggle={() =>
                  setOpenRows((rows) => ({ ...rows, [visitor.id]: !rows[visitor.id] }))
                }
              />
            ))
          )}
        </div>

        {pages > 1 && (
          <div className="sss-admin-pager">
            <span className="sss-admin-pager__meta">
              Page {page} of {pages} · {total.toLocaleString()} devices
            </span>
            <div className="sss-admin-pager__btns">
              <button
                type="button"
                className="sss-vz-btn"
                disabled={page <= 1}
                onClick={() => visit({ page: page - 1 })}
              >
                Previous
              </button>
              <button
                type="button"
                className="sss-vz-btn"
                disabled={page >= pages}
                onClick={() => visit({ page: page + 1 })}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

function Row({
  visitor,
  open,
  onToggle,
}: {
  visitor: Visitor
  open: boolean
  onToggle: () => void
}) {
  const reached = STAGE_ORDER.indexOf(visitor.topStage)
  const won = visitor.topStage === 'purchased'

  const facts: [string, string | null][] = [
    ['Device', visitor.deviceType],
    ['Client', [visitor.browser, visitor.os].filter(Boolean).join(' · ') || null],
    ['Visits', String(visitor.visits)],
    ['Events', String(visitor.events)],
    ['Landing', visitor.landing],
    ['Source', visitor.utmSource ?? 'direct'],
    ['IP', visitor.ipAddress],
    ['Language', visitor.language],
    ['Timezone', visitor.timezone],
    ['Funnel', visitor.funnel],
  ]

  return (
    <div className={`sss-vz-row ${open ? 'is-open' : ''}`}>
      <button type="button" className="sss-vz-grid sss-vz-row__main" onClick={onToggle}>
        <span className="sss-vz-id">
          <span className="sss-vz-id__n">#{visitor.id}</span>
          <span className="sss-vz-id__fp">
            {visitor.deviceFingerprint ?? 'no fingerprint'}
          </span>
        </span>

        <span className="sss-vz-progress">
          <span className="sss-vz-pips" aria-hidden>
            {STAGE_ORDER.map((name, i) => (
              <i
                key={name}
                className={[
                  'sss-vz-pip',
                  i <= reached ? 'is-on' : '',
                  won && i <= reached ? 'is-won' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            ))}
          </span>
          <span className={`sss-vz-stage ${won ? 'is-won' : ''}`}>{visitor.topStageLabel}</span>
          {visitor.events > 0 && (
            <span className="sss-vz-events">
              {visitor.events} event{visitor.events === 1 ? '' : 's'}
            </span>
          )}
        </span>

        <span className="sss-vz-cell sss-vz-hide-sm">
          {formatWhen(visitor.lastSeenAt ?? visitor.createdAt)}
        </span>

        <span className="sss-vz-cell sss-vz-hide-sm">
          {visitor.location || visitor.countryCode ? (
            <>
              <span aria-hidden>{flagEmoji(visitor.countryCode)} </span>
              {visitor.location ?? visitor.countryCode}
            </>
          ) : (
            '—'
          )}
        </span>

        <span className={`sss-vz-caret ${open ? 'is-open' : ''}`} aria-hidden>
          ⌄
        </span>
      </button>

      {open && (
        <div className="sss-vz-row__more">
          <div className="sss-vz-facts">
            {facts
              .filter(([, value]) => value)
              .map(([key, value]) => (
                <div key={key}>
                  <div className="sss-vz-fact__k">{key}</div>
                  <div className="sss-vz-fact__v">{value}</div>
                </div>
              ))}
          </div>
          <div className="sss-vz-actions">
            <button
              type="button"
              className="sss-vz-btn sss-vz-btn--solid"
              onClick={() => router.get(`/admin/visitors/${visitor.id}`)}
            >
              Open journey →
            </button>
            <CopyButton value={visitor.deviceFingerprint ?? String(visitor.id)} label="Copy ID" />
          </div>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  children,
}: {
  label: string
  value: string | number
  onChange: (value: string) => void
  children: React.ReactNode
}) {
  return (
    <label className="sss-vz-field">
      <span className="sss-vz-field__k">{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {children}
      </select>
    </label>
  )
}
